import { CommonGraphSummaryResponse, StreamlitNotebookJobsGraphData } from "@/pages/query/types";
import { useAppState } from "@/modules/app/useAppContext";
import { Card, CardContent, CardHeader } from "@altimateai/lego";
import { Chart, ChartTypes } from "@uicore";
import dayjs from "dayjs";
import { formatCost } from "@/helpers/utils";
import { Line, TooltipProps } from "recharts";
import { CommonCostChartCustomTooltip } from "@/pages/query/common/charts/CommonCostChartCustomTooltip";
import { TotalCostHeader } from "@/pages/query/common/TotalCostHeader";
import { QueryKey } from "@tanstack/react-query";

export interface CommonCostChartProps<TParams> {
  /**
   * Graph data for the chart
   */
  graphData: StreamlitNotebookJobsGraphData[];
  /**
   * Unique query key for React Query caching (for graph summary)
   */
  graphSummaryQueryKey: QueryKey;
  /**
   * Function that fetches the graph summary data
   */
  graphSummaryQueryFn: (params: TParams) => Promise<CommonGraphSummaryResponse | undefined>;
  /**
   * Parameters to pass to the graph summary query function
   */
  graphSummaryParams: TParams;
  /**
   * Custom title for the total cost header
   * @default "Total Cost"
   */
  title?: string;
}

export const CommonCostChart = <TParams,>({
  graphData = [],
  graphSummaryQueryKey,
  graphSummaryQueryFn,
  graphSummaryParams,
  title = "Total Cost",
}: CommonCostChartProps<TParams>) => {
  const { currency } = useAppState();

  return (
    <Card className={"!al-shadow-none al-border"}>
      <CardHeader>
        <TotalCostHeader
          queryKey={graphSummaryQueryKey}
          queryFn={graphSummaryQueryFn}
          params={graphSummaryParams}
          title={title}
          loaderClassName={"al-w-full"}
        />
      </CardHeader>
      <CardContent className={"al-pb-10"}>
        <Chart
          title={"Cost Over Time"}
          xAxisDataKey="date"
          yAxisLabel={{ value: "Cost" }}
          xAxisLabelFormatter={value => dayjs(value).format("MMM-DD")}
          yAxisLabelFormatter={value => formatCost(value, currency)}
          type={ChartTypes.LineChart}
          data={graphData}
          hideFullscreenButton
          tooltipProps={{
            content: (props: TooltipProps<number, string>) => (
              <CommonCostChartCustomTooltip {...props} />
            ),
          }}
          chartProps={{}}
        >
          {() => {
            return (
              <Line type="monotone" key="cost" dataKey="cost" stroke="#247EFE" strokeWidth={4} />
            );
          }}
        </Chart>
      </CardContent>
    </Card>
  );
};
